import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  Search,
  Save,
  Plus,
  Edit2,
  Trash2,
  Shield,
  UserCog,
  Loader2,
  Check,
  X,
  ChevronDown,
  Settings,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default predefined roles
const DEFAULT_ROLES = [
  { value: "president", label: "Président(e)", color: "bg-purple-100 text-purple-800" },
  { value: "vice_president", label: "Vice-Président(e)", color: "bg-indigo-100 text-indigo-800" },
  { value: "tresorier", label: "Trésorier(ère)", color: "bg-green-100 text-green-800" },
  { value: "secretaire", label: "Secrétaire", color: "bg-blue-100 text-blue-800" },
  { value: "responsable_communication", label: "Resp. Communication", color: "bg-pink-100 text-pink-800" },
  { value: "responsable_evenements", label: "Resp. Événements", color: "bg-orange-100 text-orange-800" },
  { value: "responsable_partenariats", label: "Resp. Partenariats", color: "bg-cyan-100 text-cyan-800" },
  { value: "membre_bureau", label: "Membre du Bureau", color: "bg-amber-100 text-amber-800" },
  { value: "referent_qualite", label: "Référent Qualité", color: "bg-emerald-100 text-emerald-800" },
  { value: "referent_securite", label: "Référent Sécurité", color: "bg-red-100 text-red-800" },
];

export default function RolesPage() {
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [availableRoles, setAvailableRoles] = useState(DEFAULT_ROLES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [changes, setChanges] = useState({});
  
  // Add role dialog
  const [showAddRoleDialog, setShowAddRoleDialog] = useState(false);
  const [newRoleValue, setNewRoleValue] = useState("");
  const [newRoleLabel, setNewRoleLabel] = useState("");
  
  // Edit roles dialog
  const [showEditRolesDialog, setShowEditRolesDialog] = useState(false);
  const [editingRoles, setEditingRoles] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Load food trucks with roles
      const ftResponse = await axios.get(`${API_URL}/api/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFoodTrucks(ftResponse.data);
      
      // Load available roles
      try {
        const rolesResponse = await axios.get(`${API_URL}/api/roles/available`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (rolesResponse.data.roles) {
          // Merge with default colors
          const loadedRoles = rolesResponse.data.roles.map(r => {
            const defaultRole = DEFAULT_ROLES.find(dr => dr.value === r.value);
            return {
              ...r,
              color: defaultRole?.color || "bg-gray-100 text-gray-800"
            };
          });
          setAvailableRoles(loadedRoles);
        }
      } catch (e) {
        // Use defaults if endpoint doesn't exist
        setAvailableRoles(DEFAULT_ROLES);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = (ftId, roleValue) => {
    setChanges(prev => {
      const currentRoles = prev[ftId] !== undefined 
        ? prev[ftId] 
        : (foodTrucks.find(ft => ft.id === ftId)?.roles_association || []);
      
      const newRoles = currentRoles.includes(roleValue)
        ? currentRoles.filter(r => r !== roleValue)
        : [...currentRoles, roleValue];
      
      return {
        ...prev,
        [ftId]: newRoles
      };
    });
  };

  const getCurrentRoles = (ft) => {
    return changes[ft.id] !== undefined ? changes[ft.id] : (ft.roles_association || []);
  };

  const saveAllChanges = async () => {
    if (Object.keys(changes).length === 0) {
      toast.info("Aucun changement à sauvegarder");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const updates = Object.entries(changes).map(([ftId, roles]) => ({
        food_truck_id: ftId,
        roles_association: roles
      }));

      await axios.put(`${API_URL}/api/roles/bulk`, { updates }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${updates.length} attribution(s) mise(s) à jour`);
      setChanges({});
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (value) => {
    const role = availableRoles.find(r => r.value === value);
    return role ? role.label : value;
  };

  const getRoleColor = (value) => {
    const role = availableRoles.find(r => r.value === value);
    return role?.color || "bg-gray-100 text-gray-800";
  };

  const handleAddRole = async () => {
    if (!newRoleValue.trim() || !newRoleLabel.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    // Check if role already exists
    const newKey = newRoleValue.toLowerCase().replace(/\s+/g, '_');
    if (availableRoles.some(r => r.key === newKey || r.value === newKey)) {
      toast.error("Ce rôle existe déjà");
      return;
    }

    const newRole = {
      key: newKey,
      label: newRoleLabel,
      color: "#6B7280" // Default gray color
    };

    const updatedRoles = [...availableRoles, newRole];
    
    try {
      const token = localStorage.getItem("token");
      // Send with wrapper object { roles: [...] }
      await axios.put(`${API_URL}/api/roles/available`, {
        roles: updatedRoles.map(r => ({ 
          key: r.key || r.value, 
          label: r.label, 
          color: r.color || "#6B7280" 
        }))
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setAvailableRoles(updatedRoles);
      setNewRoleValue("");
      setNewRoleLabel("");
      setShowAddRoleDialog(false);
      toast.success("Nouveau rôle ajouté");
    } catch (error) {
      console.error("Error adding role:", error);
      toast.error("Erreur lors de l'ajout du rôle");
    }
  };

  const handleDeleteRole = async (roleKey) => {
    const updatedRoles = availableRoles.filter(r => (r.key || r.value) !== roleKey);
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/api/roles/available`, {
        roles: updatedRoles.map(r => ({ 
          key: r.key || r.value, 
          label: r.label, 
          color: r.color || "#6B7280" 
        }))
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setAvailableRoles(updatedRoles);
      toast.success("Rôle supprimé");
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const hasChanges = Object.keys(changes).length > 0;

  const filteredFoodTrucks = foodTrucks.filter(ft => 
    ft.nom_food_truck?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ft.nom_contact?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group food trucks
  const foodTrucksWithRoles = filteredFoodTrucks.filter(ft => getCurrentRoles(ft).length > 0);
  const foodTrucksWithoutRoles = filteredFoodTrucks.filter(ft => getCurrentRoles(ft).length === 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-secondary flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            Rôles & Responsabilités
          </h1>
          <p className="text-muted-foreground mt-1">
            Attribuez plusieurs rôles aux membres de l'association
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowAddRoleDialog(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau rôle
          </Button>
          <Button 
            onClick={saveAllChanges} 
            disabled={!hasChanges || saving}
            className="bg-primary hover:bg-primary/90"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Sauvegarder {hasChanges && `(${Object.keys(changes).length})`}
          </Button>
        </div>
      </div>

      {/* Available Roles */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Rôles disponibles</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => {
              setEditingRoles([...availableRoles]);
              setShowEditRolesDialog(true);
            }}>
              <Settings className="w-4 h-4 mr-1" />
              Gérer
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {availableRoles.map((role) => (
              <Badge key={role.value} className={role.color}>
                {role.label}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Rechercher un food truck..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-primary">{foodTrucks.length}</div>
            <p className="text-sm text-muted-foreground">Total Food Trucks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {foodTrucks.filter(ft => (ft.roles_association || []).length > 0).length}
            </div>
            <p className="text-sm text-muted-foreground">Avec rôle(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-amber-600">
              {availableRoles.length}
            </div>
            <p className="text-sm text-muted-foreground">Rôles disponibles</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">
              {Object.keys(changes).length}
            </div>
            <p className="text-sm text-muted-foreground">Modifications</p>
          </CardContent>
        </Card>
      </div>

      {/* Food Trucks with Roles */}
      {foodTrucksWithRoles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-primary" />
              Membres avec rôle(s) ({foodTrucksWithRoles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Food Truck</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Rôles attribués</TableHead>
                  <TableHead className="text-center">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {foodTrucksWithRoles.map((ft) => (
                  <TableRow key={ft.id} className={changes[ft.id] !== undefined ? "bg-amber-50" : ""}>
                    <TableCell className="font-medium">{ft.nom_food_truck}</TableCell>
                    <TableCell>{ft.nom_contact || "-"}</TableCell>
                    <TableCell>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="h-auto py-1 px-2">
                            <div className="flex flex-wrap gap-1 max-w-[300px]">
                              {getCurrentRoles(ft).map((role) => (
                                <Badge key={role} className={`${getRoleColor(role)} text-xs`}>
                                  {getRoleLabel(role)}
                                </Badge>
                              ))}
                            </div>
                            <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-2" align="start">
                          <ScrollArea className="h-[300px]">
                            <div className="space-y-1">
                              {availableRoles.map((role) => (
                                <div
                                  key={role.value}
                                  className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                                  onClick={() => toggleRole(ft.id, role.value)}
                                >
                                  <Checkbox
                                    checked={getCurrentRoles(ft).includes(role.value)}
                                    onCheckedChange={() => toggleRole(ft.id, role.value)}
                                  />
                                  <Badge className={`${role.color} text-xs`}>
                                    {role.label}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                    <TableCell className="text-center">
                      {changes[ft.id] !== undefined ? (
                        <Badge variant="outline" className="bg-amber-100 text-amber-800">
                          Modifié
                        </Badge>
                      ) : (
                        <Check className="w-4 h-4 text-green-600 mx-auto" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Food Trucks without Roles */}
      {foodTrucksWithoutRoles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-muted-foreground" />
              Membres sans rôle ({foodTrucksWithoutRoles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Food Truck</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Attribuer des rôles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {foodTrucksWithoutRoles.map((ft) => (
                  <TableRow key={ft.id} className={changes[ft.id] !== undefined ? "bg-amber-50" : ""}>
                    <TableCell className="font-medium">{ft.nom_food_truck}</TableCell>
                    <TableCell>{ft.nom_contact || "-"}</TableCell>
                    <TableCell className="text-muted-foreground">{ft.email}</TableCell>
                    <TableCell>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Plus className="w-4 h-4 mr-1" />
                            Ajouter des rôles
                            <ChevronDown className="w-4 h-4 ml-2" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-2" align="start">
                          <ScrollArea className="h-[300px]">
                            <div className="space-y-1">
                              {availableRoles.map((role) => (
                                <div
                                  key={role.value}
                                  className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                                  onClick={() => toggleRole(ft.id, role.value)}
                                >
                                  <Checkbox
                                    checked={getCurrentRoles(ft).includes(role.value)}
                                    onCheckedChange={() => toggleRole(ft.id, role.value)}
                                  />
                                  <Badge className={`${role.color} text-xs`}>
                                    {role.label}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add Role Dialog */}
      <Dialog open={showAddRoleDialog} onOpenChange={setShowAddRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un nouveau rôle</DialogTitle>
            <DialogDescription>
              Créez un nouveau rôle pour l'association
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="roleLabel">Nom du rôle</Label>
              <Input
                id="roleLabel"
                value={newRoleLabel}
                onChange={(e) => {
                  setNewRoleLabel(e.target.value);
                  setNewRoleValue(e.target.value.toLowerCase().replace(/\s+/g, '_'));
                }}
                placeholder="Ex: Responsable Logistique"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="roleValue">Identifiant (automatique)</Label>
              <Input
                id="roleValue"
                value={newRoleValue}
                onChange={(e) => setNewRoleValue(e.target.value)}
                placeholder="responsable_logistique"
                className="mt-2"
                disabled
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRoleDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddRole}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Roles Dialog */}
      <Dialog open={showEditRolesDialog} onOpenChange={setShowEditRolesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gérer les rôles disponibles</DialogTitle>
            <DialogDescription>
              Ajoutez ou supprimez des rôles
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {availableRoles.map((role) => (
                <div key={role.value} className="flex items-center justify-between p-2 border rounded">
                  <Badge className={role.color}>{role.label}</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRole(role.value)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRolesDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
